import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2,
  Compass,
  Layers,
  Rocket,
  HandshakeIcon,
  Target,
  Workflow,
  BarChart3,
  MonitorSmartphone,
  ShieldCheck,
  Home,
  UserRound,
  Store,
  TrendingUp,
  Check,
  ArrowRight,
} from "lucide-react";
import { motion } from "framer-motion";

import { GoldButton } from "@/components/primitives";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { SectionLabel } from "@/components/primitives";
import CampaignFlowDiagram from "@/components/CampaignFlowDiagram";
import ClientsSection from "@/sections/ClientsSection";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { submitContact } from "@/lib/api";
import { contactMethods } from "@/data/content";
import {
  campaignOffer,
  CAMPAIGN_SETUP_BOOKING_URL,
  CAMPAIGN_FORM_META,
  businessTypeOptions,
  currentAdStatusOptions,
  monthlyAdBudgetOptions,
} from "@/config/campaignOffer";

const EASE = [0.22, 1, 0.36, 1];

const FIELD =
  "border-fs-border bg-transparent text-fs-ink placeholder:text-fs-muted/50 focus-visible:ring-fs-gold focus-visible:ring-1 focus-visible:border-fs-gold/60 h-11";

/* -------------------------------------------------------------------------- */
/*  Booking button (single source of truth for the campaign booking URL)      */
/* -------------------------------------------------------------------------- */
function BookCallButton({ variant = "solid", testId, children = "Book a Strategy Call" }) {
  const url = CAMPAIGN_SETUP_BOOKING_URL;
  if (!url) {
    return (
      <GoldButton onClick={() => toast.info("Booking link coming shortly.")} variant={variant} testId={testId}>
        {children}
      </GoldButton>
    );
  }
  return (
    <GoldButton
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      variant={variant}
      testId={testId}
    >
      {children}
    </GoldButton>
  );
}

/* -------------------------------------------------------------------------- */
/*  1. HERO                                                                   */
/* -------------------------------------------------------------------------- */
function Hero() {
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="campaign-hero"
      className="relative overflow-hidden bg-fs-bg pb-16 pt-32 lg:pb-24 lg:pt-40"
    >
      <div className="fs-grid-bg pointer-events-none absolute inset-0 opacity-40" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-24 top-24 h-80 w-80 rounded-full bg-fs-gold/[0.05] blur-3xl" aria-hidden="true" />

      <div className="fs-shell relative">
        <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-10">
          {/* Left */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="fs-label"
              data-testid="campaign-hero-label"
            >
              <span className="h-px w-8 bg-fs-gold/70" />
              One-Time Campaign Setup
            </motion.div>

            <motion.h1
              className="mt-7 fs-h1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.15, ease: EASE }}
            >
              Predictable lead generation
              <br className="hidden sm:block" /> starts with the right system.
              <br />
              <motion.span
                className="fs-italic-gold mt-2 block text-3xl sm:text-4xl md:text-5xl"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
              >
                We build it for you.
              </motion.span>
            </motion.h1>

            <motion.p
              className="mt-8 max-w-xl fs-body"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
            >
              Fortuna Strive sets up the campaign, lead capture, tracking, and reporting infrastructure
              your business needs to start generating and managing opportunities more consistently.
            </motion.p>
            <motion.p
              className="mt-4 max-w-xl text-sm text-fs-muted"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.95 }}
            >
              One implementation. One clear system. Built and handed over to your business.
            </motion.p>

            <motion.div
              className="mt-10 flex flex-col gap-4 sm:flex-row"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.05 }}
            >
              <BookCallButton variant="solid" testId="campaign-hero-book" />
              <a
                href="#included"
                onClick={scrollTo("included")}
                data-testid="campaign-hero-included"
                className="group inline-flex items-center justify-center gap-2.5 rounded-full border border-fs-border bg-transparent px-7 py-3.5 text-sm font-medium tracking-wide text-fs-ink transition-[background-color,border-color,color] duration-300 hover:border-fs-gold/50 hover:text-fs-gold"
              >
                See What's Included
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </motion.div>

            <motion.p
              className="mt-4 text-xs uppercase tracking-[0.18em] text-fs-muted/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.6 }}
            >
              15-minute introductory call. No commitment.
            </motion.p>
          </div>

          {/* Right — abstract campaign flow */}
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.7, ease: EASE }}
          >
            <div className="border border-fs-border bg-fs-card/60 p-6 backdrop-blur-sm lg:p-8">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.24em] text-fs-muted/70">
                  Campaign System
                </span>
                <span className="h-1.5 w-1.5 rounded-full bg-fs-gold" />
              </div>
              <CampaignFlowDiagram />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  2. PROBLEM                                                                */
/* -------------------------------------------------------------------------- */
const problems = [
  {
    n: "01",
    title: "No clear campaign structure",
    desc: "Businesses often launch ads without a clear offer, audience structure, lead journey, or tracking plan.",
  },
  {
    n: "02",
    title: "Leads arrive with nowhere to go",
    desc: "A lead only matters when there is a system to capture, organize, and respond to it quickly.",
  },
  {
    n: "03",
    title: "Agencies disappear after launch",
    desc: "Campaigns get switched on, but the business is left without visibility into what was built or how it works.",
  },
  {
    n: "04",
    title: "No reporting means no learning",
    desc: "If you cannot see where the leads came from, what they cost, and what happens after they submit, you cannot improve the system.",
  },
];

function ProblemSection() {
  return (
    <section className="border-t border-fs-border bg-fs-bg2 py-24 lg:py-32" data-testid="campaign-problem">
      <div className="fs-shell">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel>The Problem</SectionLabel>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 fs-h2">
                Running ads is easy.
                <br className="hidden sm:block" /> Building a system around them is not.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <p className="fs-body">
                Most campaigns fail long before the ad itself becomes the real problem.
              </p>
            </Reveal>
          </div>
        </div>

        <StaggerGroup className="mt-16 grid gap-6 md:grid-cols-2">
          {problems.map((p) => (
            <StaggerItem key={p.n}>
              <div className="group h-full border border-fs-border bg-fs-card p-8 transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-fs-gold/40">
                <div className="flex items-baseline justify-between">
                  <span className="font-serif text-3xl font-light text-fs-gold/70">{p.n}</span>
                </div>
                <h3 className="mt-5 font-serif text-2xl font-normal text-fs-ink">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-fs-muted">{p.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>

        <Reveal delay={0.1}>
          <p className="mt-14 max-w-2xl font-serif text-2xl font-light italic text-fs-ink sm:text-3xl">
            Ads create attention. <span className="fs-italic-gold">Systems turn that attention into opportunity.</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  3. WHAT'S INCLUDED                                                        */
/* -------------------------------------------------------------------------- */
const included = [
  {
    n: "01",
    title: "Meta Ad Account Setup",
    icon: ShieldCheck,
    bullets: [
      "Account structure review",
      "Meta Business Manager review",
      "Campaign architecture",
      "Basic tracking configuration",
      "Audience and campaign organization",
    ],
    desc: "We make sure the foundation is clean before money is spent.",
  },
  {
    n: "02",
    title: "Campaign Build & Launch",
    icon: Rocket,
    bullets: [
      "Campaign structure",
      "Ad set configuration",
      "Initial audience setup",
      "Creative placement",
      "Lead-generation or conversion objective configuration",
    ],
    desc: "One focused campaign system built specifically around the business and offer.",
  },
  {
    n: "03",
    title: "Landing Page or Lead Form",
    icon: MonitorSmartphone,
    bullets: [
      "Meta instant form or landing page connection",
      "Lead capture setup",
      "Form validation",
      "Conversion path review",
      "Mobile optimization",
    ],
    desc: "Every lead should have a clear destination and a clean path to submit their information.",
  },
  {
    n: "04",
    title: "Tracking & Lead Source Setup",
    icon: Target,
    bullets: [
      "Meta Pixel where applicable",
      "Conversion tracking",
      "UTM tracking",
      "Source attribution",
      "Form-source tracking",
    ],
    desc: "We build the tracking layer so the business can understand where leads are coming from.",
  },
  {
    n: "05",
    title: "Reporting Setup",
    icon: BarChart3,
    bullets: [
      "Simple reporting structure",
      "Campaign KPIs",
      "Lead cost visibility",
      "Campaign status",
      "Performance review structure",
    ],
    desc: "No complicated dashboards. Just the information required to understand what is working and what needs attention.",
  },
  {
    n: "06",
    title: "Handover & Post-Launch Support",
    icon: HandshakeIcon,
    bullets: [
      "Handover call",
      "Walkthrough of campaign structure",
      "Explanation of reporting",
      "Explanation of next steps",
      "Limited post-launch support period [POST-LAUNCH SUPPORT PERIOD]",
    ],
    desc: "You should understand what was built and how the system works after we hand it over.",
  },
];

function IncludedSection() {
  return (
    <section
      id="included"
      className="scroll-mt-24 border-t border-fs-border bg-fs-bg py-24 lg:py-32"
      data-testid="campaign-included"
    >
      <div className="fs-shell">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel>What's Included</SectionLabel>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 fs-h2">A complete one-time campaign setup.</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <p className="fs-body">
                We build the infrastructure required to launch the campaign properly and hand it
                over in a way your business can actually use.
              </p>
            </Reveal>
          </div>
        </div>

        <StaggerGroup className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {included.map((item) => {
            const Icon = item.icon;
            return (
              <StaggerItem key={item.n}>
                <div
                  className="group flex h-full flex-col border border-fs-border bg-fs-card p-8 transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-fs-gold/40"
                  data-testid={`campaign-included-${item.n}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="flex h-12 w-12 items-center justify-center border border-fs-border text-fs-gold transition-colors duration-300 group-hover:border-fs-gold/50">
                      <Icon className="h-5 w-5" strokeWidth={1.4} />
                    </span>
                    <span className="font-serif text-sm text-fs-muted/50">{item.n}</span>
                  </div>
                  <h3 className="mt-6 font-serif text-2xl font-normal text-fs-ink">{item.title}</h3>
                  <ul className="mt-4 space-y-2">
                    {item.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-fs-muted">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fs-gold/70" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 border-t border-fs-border pt-4 text-sm leading-relaxed text-fs-ink/80">
                    {item.desc}
                  </p>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  4. PROCESS                                                                */
/* -------------------------------------------------------------------------- */
const processSteps = [
  { n: "01", title: "Diagnose", icon: Compass, desc: "We review the business, offer, audience, existing assets, and current campaign setup." },
  { n: "02", title: "Build",    icon: Layers,  desc: "We create the campaign structure, lead capture path, tracking, and reporting system." },
  { n: "03", title: "Launch",   icon: Rocket,  desc: "We configure and launch the agreed campaign once the required assets and approvals are in place." },
  { n: "04", title: "Handover", icon: HandshakeIcon, desc: "We walk you through the system, explain how it works, and provide the agreed post-launch support." },
];

function ProcessSection() {
  return (
    <section
      className="relative overflow-hidden border-t border-fs-border bg-fs-bg2 py-24 lg:py-32"
      data-testid="campaign-process"
    >
      <div className="fs-grid-bg pointer-events-none absolute inset-0 opacity-30" aria-hidden="true" />
      <div className="fs-shell relative">
        <div className="max-w-2xl">
          <Reveal>
            <SectionLabel>The Process</SectionLabel>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 fs-h2">
              Simple engagement. <span className="fs-italic-gold">Clear outcome.</span>
            </h2>
          </Reveal>
        </div>

        <div className="mt-16 relative">
          <motion.div
            className="absolute left-6 top-0 hidden h-px w-full origin-left bg-gradient-to-r from-fs-gold/60 via-fs-gold/30 to-transparent lg:block"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute left-6 top-0 h-full w-px origin-top bg-gradient-to-b from-fs-gold/60 via-fs-gold/30 to-transparent lg:hidden"
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: "easeInOut" }}
          />

          <div className="grid gap-12 lg:grid-cols-4 lg:gap-8">
            {processSteps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.n}
                  className="relative pl-20 lg:pl-0 lg:pt-16"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.15, ease: EASE }}
                >
                  <span className="absolute left-0 top-0 flex h-12 w-12 items-center justify-center rounded-full border border-fs-gold/50 bg-fs-bg2 font-serif text-sm text-fs-gold">
                    {step.n}
                  </span>
                  <Icon className="mb-4 h-5 w-5 text-fs-gold/80 lg:mb-5" strokeWidth={1.4} />
                  <h3 className="font-serif text-2xl font-normal text-fs-ink">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-fs-muted">{step.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  5. WHO IT'S FOR                                                           */
/* -------------------------------------------------------------------------- */
const audiences = [
  {
    icon: Home,
    title: "Home Service Businesses",
    examples: "Cleaning · Flooring · HVAC · Roofing · Painting · Landscaping · Restoration · Appointment-based home services",
  },
  {
    icon: UserRound,
    title: "Consultants & Coaches",
    examples: "Business, financial and education consultants · Coaches · Professional-service providers",
  },
  {
    icon: Store,
    title: "Local Service Businesses",
    examples: "Clinics · Real estate services · Automotive · Professional local services · Appointment-driven businesses",
  },
  {
    icon: TrendingUp,
    title: "Businesses Testing Paid Acquisition",
    examples: "For companies that want to test paid advertising properly before committing to a long-term agency engagement.",
  },
];

function AudienceSection() {
  return (
    <section className="border-t border-fs-border bg-fs-bg py-24 lg:py-32" data-testid="campaign-audience">
      <div className="fs-shell">
        <Reveal>
          <SectionLabel>Who It's For</SectionLabel>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 fs-h2 max-w-3xl">
            Built for businesses that need a proper campaign foundation.
          </h2>
        </Reveal>

        <StaggerGroup className="mt-14 grid gap-px border border-fs-border bg-fs-border sm:grid-cols-2">
          {audiences.map((a) => {
            const Icon = a.icon;
            return (
              <StaggerItem key={a.title}>
                <div className="group h-full bg-fs-bg p-8 transition-colors duration-300 hover:bg-fs-card">
                  <span className="flex h-12 w-12 items-center justify-center border border-fs-border text-fs-gold transition-colors duration-300 group-hover:border-fs-gold/50">
                    <Icon className="h-5 w-5" strokeWidth={1.4} />
                  </span>
                  <h3 className="mt-6 font-serif text-2xl font-normal text-fs-ink">{a.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-fs-muted">{a.examples}</p>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  6. WHAT THIS OFFER IS NOT                                                 */
/* -------------------------------------------------------------------------- */
const notPromises = [
  {
    title: "No guaranteed lead volume",
    desc: "Advertising performance depends on the offer, market, competition, budget, pricing, sales process, and other variables.",
  },
  {
    title: "No guaranteed revenue",
    desc: "We build and launch the infrastructure. We do not promise specific financial outcomes.",
  },
  {
    title: "Ad spend is separate",
    desc: "Media budget paid to Meta is not included in the campaign setup fee unless specifically stated in the proposal.",
  },
  {
    title: "Ongoing management is separate",
    desc: "This is a one-time implementation engagement. Ongoing campaign management can be discussed separately if needed.",
  },
];

function ExpectationsSection() {
  return (
    <section className="border-t border-fs-border bg-fs-bg2 py-24 lg:py-32" data-testid="campaign-expectations">
      <div className="fs-shell">
        <div className="max-w-2xl">
          <Reveal>
            <SectionLabel>Clear Expectations</SectionLabel>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 fs-h2">
              A setup offer — <span className="fs-italic-gold">not a promise of instant growth.</span>
            </h2>
          </Reveal>
        </div>

        <StaggerGroup className="mt-14 grid gap-8 md:grid-cols-2">
          {notPromises.map((p) => (
            <StaggerItem key={p.title}>
              <div className="flex items-start gap-4">
                <span className="mt-1 h-2 w-2 shrink-0 rotate-45 bg-fs-gold" />
                <div>
                  <h3 className="font-serif text-xl text-fs-ink">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-fs-muted">{p.desc}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  7. WHY FORTUNA STRIVE                                                     */
/* -------------------------------------------------------------------------- */
const principles = [
  { icon: Compass, title: "Strategy First", desc: "We understand the offer and customer journey before building the campaign." },
  { icon: Workflow, title: "Systems Driven", desc: "We focus on how the campaign connects with the rest of the business." },
  { icon: HandshakeIcon, title: "Clear Handover", desc: "The client should understand what was built and what happens next." },
  { icon: Target, title: "Business Focused", desc: "We optimize around real business outcomes, not vanity metrics." },
];

function WhyFortunaSection() {
  return (
    <section className="border-t border-fs-border bg-fs-bg py-24 lg:py-32" data-testid="campaign-why">
      <div className="fs-shell">
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <SectionLabel>Why Fortuna Strive</SectionLabel>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 fs-h2">We build the system, not just the ad.</h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 fs-body">
                Fortuna Strive approaches lead generation as part of the wider business infrastructure.
                The campaign, website, lead capture, CRM, follow-up, and reporting all affect whether
                paid acquisition actually works.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <StaggerGroup className="grid gap-8 sm:grid-cols-2">
              {principles.map((p) => {
                const Icon = p.icon;
                return (
                  <StaggerItem key={p.title}>
                    <Icon className="h-5 w-5 text-fs-gold" strokeWidth={1.4} />
                    <h3 className="mt-4 font-serif text-xl text-fs-ink">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-fs-muted">{p.desc}</p>
                  </StaggerItem>
                );
              })}
            </StaggerGroup>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  9. OFFER DETAILS                                                          */
/* -------------------------------------------------------------------------- */
function OfferDetailsSection() {
  const rows = [
    { label: "Implementation fee", value: campaignOffer.price },
    { label: "Timeline", value: campaignOffer.implementationTimeline },
    { label: "Post-launch support", value: campaignOffer.supportPeriod },
    { label: "Media spend", value: "Paid directly by the client to the advertising platform." },
    { label: "Ongoing management", value: "Not included. Available separately if required." },
  ].filter((r) => r.value); // gracefully hide empty rows

  return (
    <section className="border-t border-fs-border bg-fs-bg2 py-24 lg:py-32" data-testid="campaign-offer-details">
      <div className="fs-shell max-w-4xl">
        <Reveal>
          <SectionLabel>One-Time Engagement</SectionLabel>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 fs-h2">Get the foundation built properly.</h2>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 border border-fs-border bg-fs-card p-8 lg:p-10">
            <dl className="divide-y divide-fs-border">
              {rows.map((r) => (
                <div key={r.label} className="grid gap-2 py-5 first:pt-0 last:pb-0 sm:grid-cols-[220px_1fr] sm:gap-6">
                  <dt className="text-xs uppercase tracking-[0.18em] text-fs-muted/70">{r.label}</dt>
                  <dd className="text-sm leading-relaxed text-fs-ink">{r.value}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-8 flex flex-col gap-4 border-t border-fs-border pt-8 sm:flex-row">
              <BookCallButton variant="solid" testId="campaign-offer-book" />
              <a
                href="#enquiry"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById("enquiry");
                  if (el) {
                    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
                    else el.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                className="group inline-flex items-center justify-center gap-2 rounded-full border border-fs-border px-6 py-3 text-sm font-medium text-fs-muted transition-colors hover:border-fs-gold/50 hover:text-fs-gold"
                data-testid="campaign-offer-enquiry"
              >
                Send an Enquiry
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  10. FINAL CTA + ENQUIRY FORM                                              */
/* -------------------------------------------------------------------------- */
const initialForm = {
  firstName: "",
  lastName: "",
  workEmail: "",
  phone: "",
  companyName: "",
  website: "",
  businessType: "",
  currentAdStatus: "",
  monthlyRevenue: "", // repurposed as ad budget in this form
  challenge: "",      // repurposed as main goal in this form
  preferredContact: "",
  privacyConsent: false,
  smsConsent: false,
  company_website_hp: "",
};

function EnquiryForm() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required.";
    if (!form.lastName.trim()) e.lastName = "Required.";
    if (!form.workEmail.trim()) e.workEmail = "Required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.workEmail)) e.workEmail = "Enter a valid email.";
    if (!form.phone.trim()) e.phone = "Required.";
    if (!form.companyName.trim()) e.companyName = "Required.";
    if (!form.businessType) e.businessType = "Please select.";
    if (!form.currentAdStatus) e.currentAdStatus = "Please select.";
    if (!form.challenge.trim()) e.challenge = "Please share your main goal.";
    if (!form.preferredContact) e.preferredContact = "Please select.";
    if (!form.privacyConsent) e.privacyConsent = "Privacy consent is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please correct the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        primaryService: "One-Time Campaign Setup",
        formName: CAMPAIGN_FORM_META.formName,
        leadSource: CAMPAIGN_FORM_META.leadSource,
      };
      const res = await submitContact(payload);
      if (res?.success) {
        setDone(true);
        toast.success("Enquiry received. We'll be in touch shortly.");
        setForm(initialForm);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch {
      toast.error("We couldn't submit your enquiry. Please try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="flex flex-col items-center justify-center border border-fs-gold/30 bg-fs-card p-14 text-center"
        data-testid="campaign-form-success"
      >
        <CheckCircle2 className="h-12 w-12 text-fs-gold" strokeWidth={1.2} />
        <h3 className="mt-6 font-serif text-3xl text-fs-ink">Thank you.</h3>
        <p className="mt-3 max-w-md fs-body">
          Your enquiry has been received. A member of the Fortuna Strive team will be in touch shortly.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="border border-fs-border bg-fs-card p-8 lg:p-10"
      data-testid="campaign-form"
    >
      {/* Honeypot */}
      <div className="hidden" aria-hidden="true">
        <label>
          Company website
          <input
            tabIndex={-1}
            autoComplete="off"
            value={form.company_website_hp}
            onChange={(e) => set("company_website_hp", e.target.value)}
          />
        </label>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field id="firstName" label="First Name" required error={errors.firstName}>
          <Input id="firstName" data-testid="campaign-input-firstName" className={FIELD} value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
        </Field>
        <Field id="lastName" label="Last Name" required error={errors.lastName}>
          <Input id="lastName" data-testid="campaign-input-lastName" className={FIELD} value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
        </Field>
        <Field id="workEmail" label="Email" required error={errors.workEmail}>
          <Input id="workEmail" type="email" data-testid="campaign-input-email" className={FIELD} value={form.workEmail} onChange={(e) => set("workEmail", e.target.value)} />
        </Field>
        <Field id="phone" label="Phone" required error={errors.phone}>
          <Input id="phone" data-testid="campaign-input-phone" className={FIELD} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </Field>
        <Field id="companyName" label="Company Name" required error={errors.companyName}>
          <Input id="companyName" data-testid="campaign-input-company" className={FIELD} value={form.companyName} onChange={(e) => set("companyName", e.target.value)} />
        </Field>
        <Field id="website" label="Website">
          <Input id="website" data-testid="campaign-input-website" placeholder="https://" className={FIELD} value={form.website} onChange={(e) => set("website", e.target.value)} />
        </Field>
        <Field id="businessType" label="Business Type" required error={errors.businessType}>
          <Select value={form.businessType} onValueChange={(v) => set("businessType", v)}>
            <SelectTrigger id="businessType" data-testid="campaign-select-businessType" className={FIELD}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="border-fs-border bg-fs-card text-fs-ink">
              {businessTypeOptions.map((o) => (
                <SelectItem key={o} value={o} className="focus:bg-fs-bg2 focus:text-fs-gold">{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field id="currentAdStatus" label="Current Advertising Status" required error={errors.currentAdStatus}>
          <Select value={form.currentAdStatus} onValueChange={(v) => set("currentAdStatus", v)}>
            <SelectTrigger id="currentAdStatus" data-testid="campaign-select-adStatus" className={FIELD}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="border-fs-border bg-fs-card text-fs-ink">
              {currentAdStatusOptions.map((o) => (
                <SelectItem key={o} value={o} className="focus:bg-fs-bg2 focus:text-fs-gold">{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field id="monthlyRevenue" label="Approximate Monthly Ad Budget" hint="Optional">
          <Select value={form.monthlyRevenue} onValueChange={(v) => set("monthlyRevenue", v)}>
            <SelectTrigger id="monthlyRevenue" data-testid="campaign-select-adBudget" className={FIELD}>
              <SelectValue placeholder="Prefer not to say" />
            </SelectTrigger>
            <SelectContent className="border-fs-border bg-fs-card text-fs-ink">
              {monthlyAdBudgetOptions.map((o) => (
                <SelectItem key={o} value={o} className="focus:bg-fs-bg2 focus:text-fs-gold">{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field id="preferredContact" label="Preferred Contact Method" required error={errors.preferredContact}>
          <Select value={form.preferredContact} onValueChange={(v) => set("preferredContact", v)}>
            <SelectTrigger id="preferredContact" data-testid="campaign-select-preferredContact" className={FIELD}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="border-fs-border bg-fs-card text-fs-ink">
              {contactMethods.map((o) => (
                <SelectItem key={o} value={o} className="focus:bg-fs-bg2 focus:text-fs-gold">{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="mt-6">
        <Field id="challenge" label="Main Goal" required error={errors.challenge}>
          <Textarea
            id="challenge"
            rows={4}
            data-testid="campaign-input-mainGoal"
            className="border-fs-border bg-transparent text-fs-ink placeholder:text-fs-muted/50 focus-visible:ring-fs-gold focus-visible:ring-1 focus-visible:border-fs-gold/60"
            placeholder="What are you hoping this campaign will achieve?"
            value={form.challenge}
            onChange={(e) => set("challenge", e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-8 space-y-5 border-t border-fs-border pt-8">
        <div className="flex items-start gap-3">
          <Checkbox
            id="privacyConsent"
            data-testid="campaign-checkbox-privacy"
            checked={form.privacyConsent}
            onCheckedChange={(v) => set("privacyConsent", v === true)}
            className="mt-1 border-fs-border data-[state=checked]:border-fs-gold data-[state=checked]:bg-fs-gold data-[state=checked]:text-[#0a0a0a]"
          />
          <Label htmlFor="privacyConsent" className="text-sm font-normal leading-relaxed text-fs-muted">
            I agree to the{" "}
            <Link to="/privacy-policy" className="text-fs-gold underline-offset-4 hover:underline">Privacy Policy</Link>{" "}
            and consent to Fortuna Strive processing my information to respond to my enquiry.{" "}
            <span className="text-fs-gold">*</span>
          </Label>
        </div>
        {errors.privacyConsent && (
          <p className="text-sm text-red-400" role="alert">{errors.privacyConsent}</p>
        )}
        <div className="flex items-start gap-3">
          <Checkbox
            id="smsConsent"
            data-testid="campaign-checkbox-sms"
            checked={form.smsConsent}
            onCheckedChange={(v) => set("smsConsent", v === true)}
            className="mt-1 border-fs-border data-[state=checked]:border-fs-gold data-[state=checked]:bg-fs-gold data-[state=checked]:text-[#0a0a0a]"
          />
          <Label htmlFor="smsConsent" className="text-xs font-normal leading-relaxed text-fs-muted/80">
            By checking this box, I agree to receive calls and text messages from Fortunastrive Services Private
            Limited regarding my enquiry, appointments, and requested services. Message frequency varies. Message
            and data rates may apply. Reply STOP to unsubscribe and HELP for assistance. Consent is not a
            condition of purchase. See our{" "}
            <Link to="/privacy-policy" className="text-fs-gold hover:underline">Privacy Policy</Link> and{" "}
            <Link to="/sms-terms" className="text-fs-gold hover:underline">SMS Terms</Link>.
          </Label>
        </div>
      </div>

      <div className="mt-8">
        <GoldButton type="submit" variant="solid" testId="campaign-form-submit" className="w-full sm:w-auto" disabled={submitting}>
          {submitting ? "Sending…" : "Send Enquiry"}
        </GoldButton>
      </div>
    </form>
  );
}

function Field({ label, id, error, required, hint, children }) {
  return (
    <div>
      <Label htmlFor={id} className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-fs-muted">
        {label}
        {required && <span className="text-fs-gold">*</span>}
        {hint && <span className="normal-case tracking-normal text-fs-muted/60">({hint})</span>}
      </Label>
      {children}
      {error && (
        <p className="mt-1.5 text-xs text-red-400" role="alert">{error}</p>
      )}
    </div>
  );
}

function FinalCTA() {
  return (
    <section
      id="enquiry"
      className="relative overflow-hidden scroll-mt-24 border-t border-fs-border bg-fs-bg py-24 lg:py-32"
      data-testid="campaign-final-cta"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-70"
        style={{
          background:
            "radial-gradient(circle, rgba(197,162,74,0.14) 0%, rgba(197,162,74,0.04) 35%, transparent 70%)",
        }}
      />
      <div className="fs-shell relative">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal>
            <h2 className="fs-h2">
              Ready to stop guessing and
              <br className="hidden sm:block" /> <span className="fs-italic-gold">build the campaign properly?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-6 max-w-xl fs-body">
              Book a short strategy call. We'll review your current situation, what you're trying to
              achieve, and whether this setup is the right fit.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <BookCallButton variant="solid" testId="campaign-final-book" />
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-4 text-xs uppercase tracking-[0.18em] text-fs-muted/70">
              15-minute introductory call. No commitment.
            </p>
          </Reveal>
        </div>

        <div className="mx-auto mt-16 max-w-3xl">
          <Reveal>
            <EnquiryForm />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  PAGE                                                                      */
/* -------------------------------------------------------------------------- */
export default function CampaignSetup() {
  return (
    <div data-testid="campaign-setup-page">
      <Hero />
      <ProblemSection />
      <IncludedSection />
      <ProcessSection />
      <AudienceSection />
      <ExpectationsSection />
      <WhyFortunaSection />
      <ClientsSection />
      <OfferDetailsSection />
      <FinalCTA />
    </div>
  );
}
